import { useMutation } from "@apollo/client";
import { FormikHelpers, Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";

import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import {
  twilioSubaccountImport,
  twilioSubaccountImportVariables
} from "src/graphql/generated/twilioSubaccountImport";
import { IMPORT_TWILIO_SUBACCOUNT } from "src/graphql/twilioSubaccount";

type Values = {
  name: string;
  accountSid: string;
  apiKey: string;
  apiSecret: string;
  authToken: string;
  shareAcrossTenants: boolean;
};

export default function InputTwilioConfig({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const snackbar = useSnackbar();

  const schema = yup.object().shape({
    name: yup.string().required("Required"),
    accountSid: yup.string().required("Required"),
    apiKey: yup.string().required("Required"),
    apiSecret: yup.string().required("Required"),
    authToken: yup.string().required("Required"),
    shareAcrossTenants: yup.boolean().required("Required")
  });

  const [createTwilioSubaccount] = useMutation<
    twilioSubaccountImport,
    twilioSubaccountImportVariables
  >(IMPORT_TWILIO_SUBACCOUNT, {
    refetchQueries: ["listTwilioSubaccounts"]
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    try {
      const { errors } = await createTwilioSubaccount({
        variables: {
          ...values
        }
      });

      if (!errors) {
        snackbar.enqueueSnackbar("Twilio configuration created.", {
          variant: "info"
        });
        helpers.resetForm({
          values: {
            name: "",
            accountSid: "",
            apiKey: "",
            apiSecret: "",
            authToken: "",
            shareAcrossTenants: false
          }
        });
      }
    } catch (errors) {
      console.error(errors);
      snackbar.enqueueSnackbar("Failed to create Twilio configuration.", {
        variant: "error"
      });
    }
  }

  return (
    <Dialog open={open}>
      <DialogTitle>Import Existing Twilio Subaccount</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            name: "",
            accountSid: "",
            apiKey: "",
            apiSecret: "",
            authToken: "",
            shareAcrossTenants: false
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Friendly Name"
                name="name"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="Account sId"
                name="accountSid"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="API Key"
                name="apiKey"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="API Secret"
                name="apiSecret"
                type="password"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="Auth Token"
                name="authToken"
                type="password"
                disabled={isSubmitting}
              />
              <FormikSwitchField
                label="Share across tenants"
                name="shareAcrossTenants"
                disabled={isSubmitting}
              />
              <DialogActions>
                <SubmitButton
                  type="submit"
                  disabled={!dirty}
                  isSubmitting={isSubmitting}
                  submitText="Import"
                  loadingText="Importing..."
                />
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="primary"
                >
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
